import React from "react";
import { ResponsiveTable } from "../ResponsiveTable";
import { Box, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { useTroubleshootingStore } from "../troubleshootingStore/troubleshootingStore";
import {
  AlarmReceiver,
  ReceiverTestRecord,
} from "../troubleshootingInterfaces";
import {
  Card,
  CardHeader,
  CELL_SX,
  EmptyCard,
  GapNotice,
  HEAD_CELL_SX,
  SHADED_HEAD_CELL_SX,
  Hint,
  Mono,
  SampleChip,
  StatusDot,
  TS_COLORS,
  TsButton,
} from "../TroubleshootingUi";
import {
  formatDateTime,
  formatRelative,
} from "../TroubleshootingUtils/formatters";
import { useScopedSites } from "../TroubleshootingUtils/useScope";
import { receiverOutcomeLabel } from "../TroubleshootingUtils/troubleshootingApi";
import { MilestoneEventServerCard } from "./MilestoneEventServerCard";

export type ReceiverTestState = Record<string, "running" | ReceiverTestRecord>;

const listeningTone = (r: AlarmReceiver) =>
  r.health.listening === "listening"
    ? "ok"
    : // A timeout says nothing answered, not that the listener refused
    // (QA AR-02): grey like unreachable, not red.
    r.health.listening === "unreachable" ||
      r.health.listening === "unknown" ||
      r.health.listening === "timed_out"
    ? "grey"
    : "bad";

export const TestResultLine = ({
  state,
  receiver,
}: {
  state: "running" | ReceiverTestRecord;
  receiver: AlarmReceiver;
}) => {
  if (state === "running") {
    return (
      <Box
        sx={{
          p: 1.5,
          display: "flex",
          gap: 1.25,
          alignItems: "center",
          border: `1px solid ${TS_COLORS.line}`,
          borderRadius: "4px",
        }}
      >
        <StatusDot
          tone="warn"
          label={
            <>
              Test alarm sent to{" "}
              <Mono>
                {receiver.host}:{receiver.port}
              </Mono>
              , waiting for the receiver…
            </>
          }
        />
      </Box>
    );
  }
  if (state.result === "pending") {
    return (
      <Box
        sx={{
          p: 1.5,
          display: "flex",
          gap: 1.25,
          alignItems: "center",
          border: `1px solid ${TS_COLORS.line}`,
          borderRadius: "4px",
        }}
      >
        <StatusDot
          tone="grey"
          label={
            <>
              <b>Sent</b> to{" "}
              <Mono>
                {receiver.host}:{receiver.port}
              </Mono>
              . The receiver has not reported back yet; the result will appear
              under Recent test alarms when it does.
            </>
          }
        />
      </Box>
    );
  }
  const ok = state.result === "delivered";
  return (
    <Box
      sx={{
        p: 1.5,
        display: "flex",
        gap: 1.25,
        alignItems: "center",
        border: `1px solid ${TS_COLORS.line}`,
        borderRadius: "4px",
        backgroundColor: ok ? "#f6fbf7" : "#fffaf5",
      }}
    >
      <StatusDot
        tone={ok ? "ok" : "bad"}
        label={
          ok ? (
            <>
              <b>Accepted by receiver</b>{" "}
              <Mono>
                {receiver.host}:{receiver.port}
              </Mono>
              . {state.detail}. The path is working end to end.
            </>
          ) : (
            <>
              <b>{receiverOutcomeLabel(state.result, state.detail)}:</b>{" "}
              {state.detail}. Nothing was accepted. Next test allowed in 60 s.
            </>
          )
        }
      />
    </Box>
  );
};

const ReceiverTables = ({
  tests,
  onTest,
  onTestAll,
  onProbe,
  probing,
}: {
  tests: ReceiverTestState;
  onTest: (receiverId: string) => void;
  onTestAll: () => void;
  /** TCP-probe the receiver's listener from the NAT proxy, then refresh. */
  onProbe: (receiverId: string) => void;
  probing: Record<string, boolean>;
}) => {
  const { receivers, receiverTests, scope } = useTroubleshootingStore();
  const scopedSites = useScopedSites();
  const scopedIds = new Set(scopedSites.map((s) => s.id));

  if (!receivers) return <EmptyCard>Loading alarm receivers…</EmptyCard>;
  if (receivers.status !== "ok") {
    return (
      <Card>
        <CardHeader
          title="Alarm receivers"
          hint="Where your alarms go once we raise them. One receiver usually serves many sites, so a receiver problem is a global problem."
        />
        <GapNotice result={receivers} what="Alarm receiver health" />
        <Box sx={{ px: 2.5, pb: 2.5 }}>
          <Hint>
            Today each camera&rsquo;s Immix or Dice destination is edited under
            the camera, and a test alert can be sent from there. This tab needs
            those destinations rolled up per host and port, with whether the
            listener accepts connections, when it last accepted an alarm, and
            how many deliveries failed in the last hour.
          </Hint>
        </Box>
      </Card>
    );
  }

  const rows = receivers.data.filter(
    (r) => scope === "all" || r.site_ids.some((id) => scopedIds.has(id))
  );
  const history = receiverTests?.status === "ok" ? receiverTests.data : [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Card>
        <CardHeader
          title="Alarm receivers"
          hint="Where your alarms go once we raise them. One receiver usually serves many sites, so a receiver problem is a global problem."
          right={
            <>
              {receivers.sample && <SampleChip />}
              <TsButton
                variant="outline"
                size="sm"
                onClick={onTestAll}
                disabled={rows.length === 0}
              >
                Send test alarm to all
              </TsButton>
            </>
          }
        />
        <Box sx={{ overflowX: "auto" }}>
          <ResponsiveTable minWidth={960}>
            <TableHead>
              <TableRow>
                {[
                  "Receiver",
                  "Address",
                  "Sites",
                  "Listening",
                  "Last accepted alarm",
                  "Failures, last hour",
                  "",
                ].map((h) => (
                  <TableCell key={h} sx={SHADED_HEAD_CELL_SX}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell sx={CELL_SX} colSpan={7}>
                    <Hint>No alarm receivers in this scope.</Hint>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => {
                const bad = listeningTone(r) === "bad";
                const fails = r.health.failures_last_hour;
                return (
                  <React.Fragment key={r.id}>
                    <TableRow
                      sx={bad ? { backgroundColor: "#fffaf5" } : undefined}
                    >
                      <TableCell sx={CELL_SX}>
                        <b>{r.label}</b>
                        <Box>
                          <Hint>
                            {r.type.replace(/_/g, " ")}
                            {r.via_connection
                              ? `, via ${r.via_connection.name}`
                              : ""}
                          </Hint>
                        </Box>
                      </TableCell>
                      <TableCell sx={CELL_SX}>
                        <Mono>
                          {r.host}:{r.port}
                        </Mono>
                      </TableCell>
                      <TableCell
                        sx={{ ...CELL_SX, fontVariantNumeric: "tabular-nums" }}
                      >
                        {r.site_ids.length}
                      </TableCell>
                      <TableCell sx={CELL_SX}>
                        <StatusDot
                          tone={listeningTone(r)}
                          label={r.health.detail}
                        />
                      </TableCell>
                      <TableCell sx={CELL_SX}>
                        {r.health.last_accepted_at
                          ? formatRelative(r.health.last_accepted_at)
                          : "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...CELL_SX,
                          fontVariantNumeric: "tabular-nums",
                          color: fails ? TS_COLORS.red : undefined,
                          fontWeight: fails ? 800 : undefined,
                        }}
                      >
                        {fails == null ? "—" : fails.toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ ...CELL_SX, whiteSpace: "nowrap" }}>
                        <TsButton
                          size="sm"
                          onClick={() => onTest(r.id)}
                          disabled={tests[r.id] === "running"}
                        >
                          Send test alarm
                        </TsButton>{" "}
                        <TsButton
                          variant="ghost"
                          size="sm"
                          onClick={() => onProbe(r.id)}
                          disabled={Boolean(probing[r.id])}
                        >
                          {probing[r.id] ? "Checking…" : "Check listener"}
                        </TsButton>
                      </TableCell>
                    </TableRow>
                    {tests[r.id] && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ pt: 0, pb: 1.5 }}>
                          <TestResultLine state={tests[r.id]} receiver={r} />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </ResponsiveTable>
        </Box>
      </Card>

      <Card>
        <CardHeader
          title="Recent test alarms"
          right={
            receiverTests?.status === "ok" && receiverTests.sample ? (
              <SampleChip />
            ) : undefined
          }
        />
        {receiverTests && receiverTests.status !== "ok" ? (
          <GapNotice result={receiverTests} what="Test alarm history" />
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <ResponsiveTable>
              <TableHead>
                <TableRow>
                  {["Sent", "Receiver", "Result", "Detail", "Sent by"].map(
                    (h) => (
                      <TableCell key={h} sx={HEAD_CELL_SX}>
                        {h}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {history.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} sx={CELL_SX}>
                      <Hint>No test alarms sent yet.</Hint>
                    </TableCell>
                  </TableRow>
                )}
                {history.map((t) => {
                  const recv = receivers.data.find(
                    (r) => r.id === t.receiver_id
                  );
                  return (
                    <TableRow key={t.id}>
                      <TableCell sx={CELL_SX}>
                        {formatDateTime(t.sent_at)}
                      </TableCell>
                      <TableCell sx={CELL_SX}>
                        {recv ? (
                          <Mono>
                            {recv.host}:{recv.port}
                          </Mono>
                        ) : (
                          t.receiver_id
                        )}
                      </TableCell>
                      <TableCell sx={CELL_SX}>
                        <StatusDot
                          tone={
                            t.result === "delivered"
                              ? "ok"
                              : t.result === "failed"
                              ? "bad"
                              : "warn"
                          }
                          label={receiverOutcomeLabel(t.result, t.detail)}
                        />
                      </TableCell>
                      <TableCell sx={CELL_SX}>{t.detail}</TableCell>
                      <TableCell sx={CELL_SX}>{t.sent_by}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </ResponsiveTable>
          </Box>
        )}
      </Card>
    </Box>
  );
};

/**
 * Immix/Dice receivers, plus, for one Milestone site, its event server:
 * Milestone alarms go straight from the connector to it, not through a
 * receiver row, so it gets its own path check.
 */
export const AlarmReceiversTab = (
  props: React.ComponentProps<typeof ReceiverTables>
) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
    <MilestoneEventServerCard />
    <ReceiverTables {...props} />
  </Box>
);
